// Channel bookmarks (shared/API.md §41.2; Mattermost server/public/model/channel_bookmark.go): the
// links/files pinned under a conversation's header. Pure rules: decoding the server's list into
// store rows, the bar's order, who may add/edit/delete/reorder (api4/channel_bookmark.go's checks),
// what a typed link becomes, and the sort_order a drag/«mover» sends.

export type BookmarkType = 'link' | 'file';

export interface ChannelBookmarkRecord {
  id: string;
  channelId: string;
  ownerId: string;
  displayName: string;
  type: BookmarkType;
  linkUrl: string;
  fileId: string;
  /** The attached file's name / mime / size (from `file`), empty for links. */
  fileName: string;
  fileMime: string;
  fileSize: number;
  emoji: string;
  imageUrl: string;
  sortOrder: number;
  createAt: number;
  updateAt: number;
}

export interface RawChannelBookmark {
  id?: string;
  channel_id?: string;
  owner_id?: string;
  display_name?: string;
  type?: string;
  link_url?: string;
  file_id?: string;
  file?: { id?: string; name?: string; mime_type?: string; size?: number } | null;
  emoji?: string;
  image_url?: string;
  sort_order?: number;
  create_at?: number;
  update_at?: number;
  delete_at?: number;
}

/**
 * The server's list → rows. Deleted entries, unknown types (`board`: externally managed, not
 * drawable here) and rows without an id are dropped; the result is in bar order.
 */
export function decodeBookmarks(raw: unknown, channelId: string): ChannelBookmarkRecord[] {
  if (!Array.isArray(raw)) return [];
  const out: ChannelBookmarkRecord[] = [];
  for (const item of raw as RawChannelBookmark[]) {
    if (!item || typeof item !== 'object' || !item.id) continue;
    if (item.delete_at) continue;
    if (item.type !== 'link' && item.type !== 'file') continue;
    if (item.channel_id && item.channel_id !== channelId) continue;
    out.push({
      id: item.id,
      channelId,
      ownerId: item.owner_id ?? '',
      displayName: item.display_name ?? '',
      type: item.type,
      linkUrl: item.link_url ?? '',
      fileId: item.file_id ?? item.file?.id ?? '',
      fileName: item.file?.name ?? '',
      fileMime: item.file?.mime_type ?? '',
      fileSize: item.file?.size ?? 0,
      emoji: item.emoji ?? '',
      imageUrl: item.image_url ?? '',
      sortOrder: typeof item.sort_order === 'number' ? item.sort_order : 0,
      createAt: item.create_at ?? 0,
      updateAt: item.update_at ?? 0,
    });
  }
  return sortBookmarks(out);
}

/** Bar order: `sort_order`, then creation, then id (the webapp's order). */
export function sortBookmarks<T extends Pick<ChannelBookmarkRecord, 'sortOrder' | 'createAt' | 'id'>>(list: readonly T[]): T[] {
  return [...list].sort((a, b) => a.sortOrder - b.sortOrder || a.createAt - b.createAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

export interface BookmarkRights {
  add: boolean;
  edit: boolean;
  delete: boolean;
  order: boolean;
}

export const NO_BOOKMARK_RIGHTS: BookmarkRights = { add: false, edit: false, delete: false, order: false };

/**
 * What the reader may do in this conversation (api4/channel_bookmark.go): public/private channels
 * by the `*_bookmark_public_channel` / `*_bookmark_private_channel` permissions; DMs and group
 * messages by membership, except guests; an archived channel by nobody. `available` = the server
 * answered the list at all (an unlicensed server answers 501 to every bookmark route — nothing to
 * offer then).
 */
export function bookmarkRights(input: { channelType: string; archived: boolean; permissions: ReadonlySet<string>; isGuest: boolean; available: boolean }): BookmarkRights {
  if (!input.available || input.archived) return NO_BOOKMARK_RIGHTS;
  if (input.channelType === 'D' || input.channelType === 'G') {
    const ok = !input.isGuest;
    return { add: ok, edit: ok, delete: ok, order: ok };
  }
  const scope = input.channelType === 'O' ? 'public' : input.channelType === 'P' ? 'private' : null;
  if (!scope) return NO_BOOKMARK_RIGHTS;
  const has = (verb: string) => input.permissions.has(`${verb}_bookmark_${scope}_channel`);
  return { add: has('add'), edit: has('edit'), delete: has('delete'), order: has('order') };
}

/**
 * A typed link → the URL the server will accept (`IsValidHTTPURL`): a bare host gets `https://`;
 * anything that is not http(s) after that (`javascript:`, `mailto:` …) is refused (null).
 */
export function normalizeBookmarkUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed || /\s/.test(trimmed)) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`;
  try {
    const u = new URL(withScheme);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    if (!u.hostname || !u.hostname.includes('.') && u.hostname !== 'localhost') return null;
    return withScheme;
  } catch {
    return null;
  }
}

/** The name a link gets when the reader leaves it empty: its host without `www.`. */
export function defaultBookmarkName(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/** The server caps `display_name` at 64 runes (model.DisplayNameMaxRunes for bookmarks). */
export const BOOKMARK_NAME_MAX = 64;

/**
 * Local reorder: `id` moved to `toIndex` in the bar → the new order (what the store shows at once)
 * and the `sort_order` value the server's `/sort_order` route takes (the target index).
 */
export function moveBookmark<T extends { id: string; sortOrder: number }>(list: readonly T[], id: string, toIndex: number): { order: T[]; sortOrder: number } | null {
  const from = list.findIndex((b) => b.id === id);
  if (from < 0) return null;
  const target = Math.max(0, Math.min(list.length - 1, toIndex));
  if (target === from) return null;
  const order = [...list];
  const [moved] = order.splice(from, 1);
  order.splice(target, 0, moved);
  return { order: order.map((b, i) => ({ ...b, sortOrder: i })), sortOrder: target };
}

export const BOOKMARK_EVENTS = ['channel_bookmark_created', 'channel_bookmark_updated', 'channel_bookmark_deleted', 'channel_bookmark_sorted'] as const;

export function isBookmarkEvent(event: string): boolean {
  return (BOOKMARK_EVENTS as readonly string[]).includes(event);
}
